import { AttachmentCategoryEnum } from '@form-engine/bso-cxp-attachments-client';
import { i18n } from '@form-engine/builder-application/index';
import BuilderArrayFormControl, {
    BuilderArrayFormControlColConfig,
} from '@form-engine/builder-array-form-control';
import BuilderArrayFormControlCell, {
    BuilderArrayFormControlCellProps,
} from '@form-engine/builder-array-form-control/components/array-form-control/cell';
import BuilderProperty from '@form-engine/builder-mask/components/mask/property';
import BuilderEnumProperty from '@form-engine/builder-mask/components/properties/enum';
import BuilderStringProperty, {
    BuilderStringPropertyProps,
} from '@form-engine/builder-mask/components/properties/string';
import {
    MaskContextValue,
    MaskProvider,
    useMaskProperty,
} from '@form-engine/builder-mask/hooks/use-mask';
import { useMaskPropertyPermission } from '@form-engine/builder-mask/hooks/use-mask-config';
import { useMaskPropertyError } from '@form-engine/builder-mask/hooks/use-mask-validation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiName } from '../attachments-element';
import { ElementDefinitionSubType } from '@form-engine/core-application/types/form';
import BuilderBooleanProperty from '@form-engine/builder-mask/components/properties/boolean';

type AttachmentsInputListSettings = Exclude<
    ElementDefinitionSubType[typeof apiName]['listSettings'],
    undefined
>;

const { Other: DefaultCateogry, ...Categories } = AttachmentCategoryEnum;

interface AttachmentsInputListSettingsCellMaskProps {
    value?: AttachmentsInputListSettings;
    onChange: (u: AttachmentsInputListSettings) => void;
}

function AttachmentsInputListSettingsCellMask({
    value,
    onChange,
}: AttachmentsInputListSettingsCellMaskProps) {
    const { c } = i18n.prefix(`mask.${apiName}.`);

    const [state, setState] = useState(value);

    useEffect(() => {
        setState(value);
    }, [value, setState]);

    const setItem = useCallback(
        (newValue: AttachmentsInputListSettings) => {
            setState(newValue);
            onChange?.(newValue);
        },
        [setState, onChange]
    );

    const setItemProperty: <K extends keyof AttachmentsInputListSettings>(
        k: K,
        v: AttachmentsInputListSettings[K]
    ) => void = useCallback(
        (propertyName, newValue) => {
            setItem({
                ...(value ?? {}),
                [propertyName]: newValue,
            } as AttachmentsInputListSettings);
        },
        [setItem, value]
    );

    const api = useMemo(
        () => ({
            state,
            setItem,
            setItemProperty,
        }),
        [state, setItem]
    );

    return (
        <MaskProvider value={api as unknown as MaskContextValue}>
            <BuilderEnumProperty
                propertyName="bsoCategory"
                label={c('bsoCategory')}
                options={[
                    ['', DefaultCateogry],
                    ...Object.values(Categories).map((value) => [value]),
                ]}
            />

            <BuilderStringProperty
                propertyName="accept"
                label={c('accept')}
                placeholder={c('acceptPlaceholder')}
            />

            <BuilderStringProperty
                type="number"
                step="1"
                propertyName="validationMaxAttachmentSize"
                label={c('validationMaxAttachmentSize')}
            />

            <BuilderStringProperty
                type="number"
                step="1"
                defaultValue="1"
                propertyName="validationMaxAttachments"
                label={c('validationMaxAttachments')}
            />

            <BuilderBooleanProperty
                propertyName="required"
                label={c('required')}
            />
        </MaskProvider>
    );
}

function AttachmentsInputListSettingsCell({
    value,
    onChange,
    ...cellProps
}: BuilderArrayFormControlCellProps &
    AttachmentsInputListSettingsCellMaskProps) {
    return (
        <BuilderArrayFormControlCell {...cellProps}>
            <AttachmentsInputListSettingsCellMask
                {...{
                    value,
                    onChange,
                }}
            />
        </BuilderArrayFormControlCell>
    );
}

export type BuilderExpressionsPropertyProps = Omit<
    BuilderStringPropertyProps,
    'propertyName'
> & {
    propertyName?: string;
};

export default function BsoAttachmentsInputListSettingsProperty({
    label,
    propertyName = 'listSettings',
    ...props
}: BuilderExpressionsPropertyProps) {
    const { c } = i18n.prefix(`mask.${apiName}.`);

    const error = useMaskPropertyError(propertyName);
    const permission = useMaskPropertyPermission(propertyName);
    const [value, setValue] = useMaskProperty(propertyName, []);

    const cols = useMemo<{
        [key: string]: BuilderArrayFormControlColConfig;
    }>(() => {
        return {
            expressions: {
                asModel: true,
                cellComponent: AttachmentsInputListSettingsCell,
            } as BuilderArrayFormControlColConfig,
        };
    }, []);

    return (
        <BuilderProperty
            className="feb-bso-attachments-input-list-settings-property"
            stacked
            label={label ?? c(propertyName)}
            error={error?.message}
            {...props}
        >
            <BuilderArrayFormControl
                head={false}
                schema={'object'}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                cols={cols as any}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value={value as any}
                onChange={setValue}
                disabled={permission === 'read'}
            />
        </BuilderProperty>
    );
}
